import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getClaudeTokens,
  createClaudeToken,
  updateClaudeToken,
  getClaudeTokenUsage,
  getClaudeTokenErrors,
  revokeClaudeToken,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { KernelPage } from '../kernelAdminShared';
import { fmtDate, fmtDateTime, parseDbDate } from '../utils/kernelFormat';
import '../components/kernelClaude.css';

// Scadenze proposte (giorni da oggi); "0" = non scade mai.
const EXPIRY_CHOICES = ['30', '90', '365'];
const EXPIRING_DAYS = 7;
// Assistenti collegabili: stesso server MCP e stessi token, ognuno si abilita per azienda ({nome}_enabled).
const CONNECTORS = ['claude', 'gemini', 'chatgpt'];
const STATUS_BADGE = { active: 'badge-success', expired: 'badge-warning', revoked: 'badge-warning' };

/** Barre degli ultimi giorni di un token: richieste (e quante con errore). */
function UsageBars({ days, t }) {
  const max = Math.max(1, ...days.map((d) => d.requests));
  return (
    <div className="kc-usage" aria-label={t('claude.usageDaily')}>
      {days.map((d) => (
        <div key={d.day} className="kc-usage-col" title={t('claude.usageDay', { day: d.day.slice(8, 10) + '/' + d.day.slice(5, 7), requests: d.requests, errors: d.errors })}>
          <div className="kc-usage-bar" style={{ height: `${Math.max(d.requests > 0 ? 4 : 1, (d.requests / max) * 40)}px` }}>
            {d.errors > 0 && <div className="kc-usage-err" style={{ height: `${(d.errors / d.requests) * 100}%` }} />}
          </div>
          <span>{d.day.slice(8, 10)}</span>
        </div>
      ))}
    </div>
  );
}

/** Elenco a tendina: pulsante + menu (al posto del select nativo), posizionato in modo da non essere tagliato dalle tabelle. */
function KcDropdown({ value, options, onChange, placeholder, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('.kc-dd-menu')) return;
      close();
    };
    const onKey = (e) => e.key === 'Escape' && close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const roomBelow = window.innerHeight - r.bottom;
      setPos({ left: r.left, minWidth: r.width, ...(roomBelow < 230 ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    }
    setOpen(!open);
  };

  const current = options.find((o) => o.value === value);
  return (
    <>
      <button type="button" ref={btnRef} className={'kc-dd-btn' + (open ? ' kc-dd-btn-open' : '')} aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} onClick={toggle}>
        <span>{current ? current.label : placeholder}</span>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && pos && (
        <ul className="kc-dd-menu" role="listbox" style={pos}>
          {options.map((o) => (
            <li key={o.value}>
              <button type="button" role="option" aria-selected={o.value === value} className={'kc-dd-item' + (o.value === value ? ' kc-dd-item-active' : '')} onClick={() => { setOpen(false); onChange(o.value); }}>
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** Riquadro di codice con il pulsante "Copia". */
function Snippet({ text, t }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // niente appunti disponibili: il testo resta selezionabile a mano
    }
  };
  return (
    <div className="kc-secret-code">
      <pre className="kc-snippet" style={{ flex: 1, margin: 0 }}><code>{text}</code></pre>
      <button className="btn btn-secondary btn-sm" type="button" onClick={copy}>{copied ? t('claude.copied') : t('claude.copy')}</button>
    </div>
  );
}

/**
 * Configurazione per Gemini CLI: il token sta in una variabile d'ambiente e viaggia nell'header Authorization
 * (il server MCP accetta "Authorization: Bearer"), quindi non resta scritto nel file di configurazione.
 */
function GeminiSnippets({ token, baseUrl, t }) {
  const config = JSON.stringify({ mcpServers: { app: { httpUrl: baseUrl, headers: { Authorization: 'Bearer $APP_MCP_TOKEN' }, trust: false } } }, null, 2);
  return (
    <>
      <p className="kc-hint">{t('claude.geminiEnvTitle')}</p>
      <Snippet text={`setx APP_MCP_TOKEN "${token}"`} t={t} />
      <p className="kc-hint">{t('claude.geminiConfigTitle')}</p>
      <Snippet text={config} t={t} />
    </>
  );
}

/** Ultimi errori di un token: quando, di che tipo, quale strumento e il messaggio. */
function ErrorList({ errors, t }) {
  if (errors.length === 0) return <p className="kc-hint">{t('claude.errorsEmpty')}</p>;
  return (
    <>
      <div className="table-wrap">
        <table className="table kc-err-table">
          <thead>
            <tr>
              <th>{t('claude.errWhen')}</th>
              <th>{t('claude.errKind')}</th>
              <th>{t('claude.errTool')}</th>
              <th>{t('claude.errMessage')}</th>
              <th>{t('claude.errIp')}</th>
            </tr>
          </thead>
          <tbody>
            {errors.map((e) => (
              <tr key={e.id}>
                <td className="nowrap">{fmtDateTime(e.created_at)}</td>
                <td><span className="badge badge-warning">{t(`claude.errKind_${e.kind}`)}</span></td>
                <td>{e.tool ? <code className="kc-code">{e.tool}</code> : '—'}</td>
                <td className="kc-err-msg">{e.message}</td>
                <td className="nowrap">{e.ip || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="kc-hint">{t('claude.errorsNote')}</p>
    </>
  );
}

/**
 * Pagina "Claude" di ogni app: token personali per collegare Claude (server MCP dell'app) ai propri dati,
 * con gli stessi permessi dell'utente. Ognuno gestisce i suoi; chi gestisce gli utenti vede e può revocare
 * anche quelli dell'azienda (ad esempio per disattivare l'accesso di una persona).
 */
function ClaudeConnector({ user, onLogout }) {
  const { t } = useLanguage();
  const [tokens, setTokens] = useState([]);
  const [canManageAll, setCanManageAll] = useState(false);
  const [scope, setScope] = useState('mine');
  const [mcpUrl, setMcpUrl] = useState('');
  const [rateLimit, setRateLimit] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ name: '', days: '90', access: 'read', ips: '' });
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [copied, setCopied] = useState(false);
  const [usageOpen, setUsageOpen] = useState({});
  const [errorsOpen, setErrorsOpen] = useState({});
  const [chosen, setChosen] = useState('');
  // Una sessione senza il campo (salvata prima di questa funzione) conta come abilitata: il server rifiuta comunque.
  const available = CONNECTORS.filter((c) => !(user && (user[`${c}_enabled`] === 0 || user[`${c}_enabled`] === false || user[`${c}_enabled`] === '0')));
  const client = available.includes(chosen) ? chosen : (available[0] || 'claude');

  const load = useCallback(async (nextScope) => {
    setLoading(true);
    try {
      const data = await getClaudeTokens(nextScope);
      setTokens(data.tokens || []);
      setCanManageAll(!!data.can_manage_all);
      setMcpUrl(data.mcp_url || '');
      setRateLimit(data.rate_limit_per_minute ? String(data.rate_limit_per_minute) : '');
    } catch (err) {
      setError(errorMessage(err, t('claude.loadError')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(scope);
  }, [scope, load]);

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    resetMessages();
    setCreating(true);
    try {
      const res = await createClaudeToken({ name: form.name.trim(), expires_in_days: Number(form.days) || null, access: form.access, allowed_ips: form.ips.trim() });
      setRevealed({ name: form.name.trim(), url: res.connector_url });
      setForm({ ...form, name: '', ips: '' });
      await load(scope);
    } catch (err) {
      setError(errorMessage(err, t('claude.createError')));
    } finally {
      setCreating(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(revealed.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      setError(t('claude.copyError'));
    }
  };

  const handleRevoke = async (token) => {
    const message = token.is_mine
      ? t('claude.confirmRevoke', { name: token.name })
      : t('claude.confirmRevokeOther', { name: token.name, owner: `${token.owner_name} ${token.owner_surname}`.trim() });
    if (!window.confirm(message)) return;
    resetMessages();
    try {
      await revokeClaudeToken(token.id);
      setSuccess(t('claude.revoked'));
      await load(scope);
    } catch (err) {
      setError(errorMessage(err, t('claude.revokeError')));
    }
  };

  const handleExpiry = async (token, days) => {
    if (days === '') return;
    resetMessages();
    try {
      await updateClaudeToken(token.id, { expires_in_days: Number(days) || null });
      setSuccess(t('claude.expiryUpdated'));
      await load(scope);
    } catch (err) {
      setError(errorMessage(err, t('claude.expiryError')));
    }
  };

  const handleAccess = async (token, access) => {
    if (access === '' || access === (token.access || 'write')) return;
    resetMessages();
    try {
      await updateClaudeToken(token.id, { access });
      setSuccess(t('claude.accessUpdated'));
      await load(scope);
    } catch (err) {
      setError(errorMessage(err, t('claude.accessError')));
    }
  };

  const handleIps = async (token) => {
    const answer = window.prompt(t('claude.ipsPrompt'), token.allowed_ips ? token.allowed_ips.split(',').join(', ') : '');
    if (answer === null) return;
    resetMessages();
    try {
      await updateClaudeToken(token.id, { allowed_ips: answer.trim() });
      setSuccess(t('claude.ipsUpdated'));
      await load(scope);
    } catch (err) {
      setError(errorMessage(err, t('claude.ipsError')));
    }
  };

  const toggleUsage = async (token) => {
    if (usageOpen[token.id]) {
      setUsageOpen({ ...usageOpen, [token.id]: null });
      return;
    }
    try {
      const res = await getClaudeTokenUsage(token.id, 14);
      setUsageOpen({ ...usageOpen, [token.id]: res.days });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const toggleErrors = async (token) => {
    if (errorsOpen[token.id]) {
      setErrorsOpen({ ...errorsOpen, [token.id]: null });
      return;
    }
    try {
      const res = await getClaudeTokenErrors(token.id, 50);
      setErrorsOpen({ ...errorsOpen, [token.id]: res.errors || [] });
    } catch (err) {
      setError(errorMessage(err, t('claude.errorsLoadError')));
    }
  };

  const expiryOptions = EXPIRY_CHOICES.map((d) => ({ value: d, label: t(`claude.exp_${d}`) }));
  const accessOptions = [{ value: 'write', label: t('claude.access_write') }, { value: 'read', label: t('claude.access_read') }];

  // Token attivi che scadono a breve: avviso in cima alla pagina.
  const expiring = tokens.filter((tk) => tk.status === 'active' && tk.expires_at && (parseDbDate(tk.expires_at).getTime() - Date.now()) < EXPIRING_DAYS * 86400000);
  const expiryText = (token) => (token.expires_at ? fmtDate(token.expires_at) : t('claude.never'));
  const ownerName = (token) => `${token.owner_name} ${token.owner_surname}`.trim();

  return (
    <KernelPage user={user} onLogout={onLogout}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('claude.title')}</h1>
          <p className="page-subtitle">{t('claude.subtitle')}</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <section className="card kc-card">
        <div className="kc-list-head">
          <h3 className="kc-title">{t('claude.howTitle')}</h3>
          {available.length > 1 && (
            <div className="kc-scope" role="group" aria-label={t('claude.clientLabel')}>
              {available.map((c) => (
                <button key={c} type="button" className={'btn btn-sm ' + (client === c ? '' : 'btn-secondary')} aria-pressed={client === c} onClick={() => setChosen(c)}>{t(`claude.client_${c}`)}</button>
              ))}
            </div>
          )}
        </div>
        {client === 'claude' && (
          <>
            <ol className="kc-steps">
              <li>{t('claude.step1')}</li>
              <li>{t('claude.step2')}</li>
              <li>{t('claude.step3')}</li>
            </ol>
            <p className="kc-hint">{t('claude.howHint')}</p>
          </>
        )}
        {client === 'gemini' && (
          <>
            <ol className="kc-steps">
              <li>{t('claude.geminiStep1')}</li>
              <li>{t('claude.geminiStep2')}</li>
              <li>{t('claude.geminiStep3')}</li>
              <li>{t('claude.geminiStep4')}</li>
            </ol>
            <GeminiSnippets token={revealed ? revealed.url.split('/').pop() : t('claude.geminiTokenPlaceholder')} baseUrl={mcpUrl || 'https://…/mcp'} t={t} />
            <p className="kc-hint">{t('claude.geminiHint')}</p>
          </>
        )}
        {client === 'chatgpt' && (
          <>
            <ol className="kc-steps">
              <li>{t('claude.chatgptStep1')}</li>
              <li>{t('claude.chatgptStep2')}</li>
              <li>{t('claude.chatgptStep3')}</li>
              <li>{t('claude.chatgptStep4')}</li>
            </ol>
            <p className="kc-hint">{t('claude.chatgptAddress')}</p>
            <pre className="kc-snippet"><code>{revealed ? revealed.url : `${mcpUrl || 'https://…/mcp'}/${t('claude.geminiPlaceholder')}`}</code></pre>
            <p className="kc-hint">{t('claude.chatgptHint')}</p>
          </>
        )}
        {mcpUrl && <p className="kc-hint">{t('claude.baseUrl')} <code className="kc-code">{mcpUrl}/&lt;token&gt;</code></p>}
      </section>

      {expiring.length > 0 && (
        <div className="warning-message" role="alert">
          <strong>{t('claude.expiringTitle')}</strong>
          <ul className="kc-list">
            {expiring.map((tk) => (
              <li key={tk.id}>{t('claude.expiringItem', { name: tk.name, owner: ownerName(tk), date: fmtDateTime(tk.expires_at) })}</li>
            ))}
          </ul>
          <span>{t('claude.expiringHint')}</span>
        </div>
      )}

      {revealed && (
        <div className="card kc-secret">
          <h3 className="kc-title">{t('claude.revealTitle')} — {revealed.name}</h3>
          <p className="kc-hint">{t('claude.revealHint')}</p>
          <div className="kc-secret-code">
            <code>{revealed.url}</code>
            <button className="btn btn-secondary btn-sm" type="button" onClick={copyUrl}>
              {copied ? t('claude.copied') : t('claude.copy')}
            </button>
          </div>
          <button className="btn" type="button" onClick={() => setRevealed(null)}>{t('claude.revealClose')}</button>
        </div>
      )}

      <section className="card kc-card">
        <h3 className="kc-title">{t('claude.newTitle')}</h3>
        <form className="kc-new" onSubmit={handleCreate}>
          <div className="form-group kc-new-name">
            <label>{t('claude.tokenName')}</label>
            <input value={form.name} maxLength={100} placeholder={t('claude.tokenNamePlaceholder')} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="form-group kc-new-expiry">
            <label>{t('claude.expiry')}</label>
            <KcDropdown value={form.days} options={expiryOptions} ariaLabel={t('claude.expiry')} onChange={(v) => setForm({ ...form, days: v })} />
          </div>
          <div className="form-group kc-new-expiry">
            <label>{t('claude.access')}</label>
            <KcDropdown value={form.access} options={accessOptions} ariaLabel={t('claude.access')} onChange={(v) => setForm({ ...form, access: v })} />
          </div>
          <div className="form-group kc-new-ips">
            <label>{t('claude.allowedIps')}</label>
            <input value={form.ips} placeholder={t('claude.allowedIpsPlaceholder')} onChange={(e) => setForm({ ...form, ips: e.target.value })} />
          </div>
          <button type="submit" className="btn" disabled={creating || form.name.trim() === ''}>
            {creating ? t('common.saving') : t('claude.create')}
          </button>
        </form>
        <p className="kc-hint">{t('claude.expiryHint')}</p>
        <p className="kc-hint">{t('claude.accessHint')}</p>
        <p className="kc-hint">{t('claude.allowedIpsHint')}</p>
      </section>

      <section className="card kc-card">
        <div className="kc-list-head">
          <h3 className="kc-title">{scope === 'company' ? t('claude.companyTokens') : t('claude.myTokens')}</h3>
          {canManageAll && (
            <div className="kc-scope" role="group" aria-label={t('claude.scope')}>
              <button type="button" className={'btn btn-sm ' + (scope === 'mine' ? '' : 'btn-secondary')} onClick={() => setScope('mine')}>{t('claude.scopeMine')}</button>
              <button type="button" className={'btn btn-sm ' + (scope === 'company' ? '' : 'btn-secondary')} onClick={() => setScope('company')}>{t('claude.scopeCompany')}</button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : tokens.length === 0 ? (
          <p className="kc-hint">{t('claude.noTokens')}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('claude.colName')}</th>
                  {scope === 'company' && <th>{t('claude.colOwner')}</th>}
                  <th>{t('claude.colStatus')}</th>
                  <th>{t('claude.colExpires')}</th>
                  <th>{t('claude.colLastUsed')}</th>
                  <th>{t('claude.colUsage')}</th>
                  <th>{t('claude.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <React.Fragment key={token.id}>
                  <tr className={token.status === 'active' ? '' : 'row-inactive'}>
                    <td>
                      <div className="kc-token-name">
                        {token.name}
                        {token.access === 'read' && <span className="badge badge-warning kc-badge">{t('claude.badgeRead')}</span>}
                        {token.allowed_ips && <span className="badge badge-success kc-badge" title={token.allowed_ips}>{t('claude.badgeIps', { n: token.allowed_ips.split(',').length })}</span>}
                      </div>
                      <div className="kc-muted">{token.token_prefix}… · {t('claude.createdOn', { date: fmtDate(token.created_at) })}</div>
                    </td>
                    {scope === 'company' && (
                      <td>
                        {ownerName(token)}
                        {token.is_mine && <span className="kc-muted"> ({t('claude.you')})</span>}
                      </td>
                    )}
                    <td><span className={`badge ${STATUS_BADGE[token.status] || 'badge-warning'}`}>{t(`claude.status_${token.status}`)}</span></td>
                    <td className="nowrap">{expiryText(token)}</td>
                    <td className="nowrap">{token.last_used_at ? fmtDateTime(token.last_used_at) : <span className="kc-muted">{t('claude.neverUsed')}</span>}</td>
                    <td>
                      <button type="button" className="kc-usage-btn" onClick={() => toggleUsage(token)} title={t('claude.usageShow')}>
                        <strong>{token.usage.today}</strong> <span className="kc-muted">{t('claude.usageToday')}</span>
                        <span className="kc-muted"> · {token.usage.last7} {t('claude.usage7')}</span>
                        </button>
                      {token.usage.errors7 > 0 && (
                        <button type="button" className="kc-usage-errors kc-errors-btn" onClick={() => toggleErrors(token)} title={t('claude.errorsShow')}>
                          {token.usage.errors7} {t('claude.usageErrors')} ›
                        </button>
                      )}
                      {usageOpen[token.id] && <UsageBars days={usageOpen[token.id]} t={t} />}
                    </td>
                    <td>
                      {token.status !== 'revoked' && (
                        <div className="kc-actions">
                          <KcDropdown value="" options={expiryOptions} placeholder={t('claude.changeExpiry')} ariaLabel={t('claude.changeExpiry')} onChange={(v) => handleExpiry(token, v)} />
                          <KcDropdown value="" options={accessOptions} placeholder={t('claude.changeAccess')} ariaLabel={t('claude.changeAccess')} onChange={(v) => handleAccess(token, v)} />
                          <button className="btn btn-secondary btn-sm" type="button" onClick={() => handleIps(token)} title={t('claude.editIpsTitle')}>
                            {t('claude.editIps')}
                          </button>
                          <button className="btn btn-secondary btn-sm" type="button" onClick={() => handleRevoke(token)} title={t('claude.revokeTitle')}>
                            {t('claude.revoke')}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {errorsOpen[token.id] && (
                    <tr className="kc-detail-row">
                      <td colSpan={scope === 'company' ? 7 : 6}>
                        <div className="kc-detail-head">
                          <strong>{t('claude.errorsTitle', { name: token.name })}</strong>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => toggleErrors(token)}>{t('claude.errorsClose')}</button>
                        </div>
                        <ErrorList errors={errorsOpen[token.id]} t={t} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {rateLimit !== '' && <p className="kc-hint">{t('claude.rateLimitInfo', { n: rateLimit })}</p>}
    </KernelPage>
  );
}

export default ClaudeConnector;
